import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { aboutTnoNetworkContent } from '../../../data/aboutTnoNetworkContent';
import { AboutPillarIllustration } from './AboutPageIllustrations';
import {
  BroadcastScreenVector,
  SignalWavesVector,
  NicheGridVector,
} from '../TNOMediaKit/components/GrillaHeroVectors';
import { getTnoHeroGradient } from '../../../theme/tnoBrandColors';

const TNO_SITE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_TNO_SITE_URL) ||
  'https://tnonetwork.com';

const LOGO_LIGHT = '/images/logos/logo_white.PNG';
const LOGO_DARK = '/images/logos/logo_tnonetwork_black.png';

const heroLineSx = {
  fontWeight: 900,
  lineHeight: 1.05,
  letterSpacing: '-0.03em',
  fontSize: { xs: '1.85rem', sm: '2.5rem', md: '3rem', lg: '3.45rem' },
};

const floatKeyframes = {
  '@keyframes aboutFloatA': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-12px)' },
  },
  '@keyframes aboutFloatB': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-8px)' },
  },
};

const LandingAboutTNO = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const isDark = themeMode === 'dark';
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const logoSrc = isDark ? LOGO_LIGHT : LOGO_DARK;

  const pageBg = isDark ? '#030712' : '#f8fafc';
  const fg = isDark ? '#f8fafc' : '#0f172a';
  const muted = isDark ? 'rgba(248,250,252,0.78)' : 'rgba(15,23,42,0.72)';
  const headerBg = isDark ? 'rgba(3,7,18,0.92)' : 'rgba(255,255,255,0.92)';
  const headerBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)';

  const heroGradient = getTnoHeroGradient(primary, secondary);

  return (
    <Box sx={{ bgcolor: pageBg, color: fg, minHeight: '100vh', ...floatKeyframes }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 6 },
          py: 1.5,
          borderBottom: `1px solid ${headerBorder}`,
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: headerBg,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="none"
          sx={{ display: 'flex', alignItems: 'center' }}
          aria-label="TNO Network — inicio media kit"
        >
          <Box
            component="img"
            src={logoSrc}
            alt="TNO Network"
            sx={{ height: { xs: 36, md: 44 }, width: 'auto', display: 'block' }}
          />
        </Link>
        <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 3 }, alignItems: 'center' }}>
          <Link
            component={RouterLink}
            to="/programacion"
            underline="hover"
            sx={{ color: muted, fontSize: '0.9rem', fontWeight: 600 }}
          >
            Programación
          </Link>
          <Link
            component={RouterLink}
            to="/paquetes"
            underline="hover"
            sx={{ color: muted, fontSize: '0.9rem', fontWeight: 600 }}
          >
            Paquetes
          </Link>
          <Link
            component={RouterLink}
            to="/media-kits"
            underline="hover"
            sx={{ color: muted, fontSize: '0.9rem', fontWeight: 600 }}
          >
            Media Kits
          </Link>
          <Link
            href={TNO_SITE}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ color: muted, fontSize: '0.9rem', fontWeight: 600 }}
          >
            tnonetwork.com
          </Link>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 6 }, pt: 2, pb: 1 }}>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackIcon />}
          color="primary"
          variant="outlined"
          sx={{
            borderRadius: 999,
            fontWeight: 700,
            borderWidth: 2,
            color: isDark ? '#f8fafc' : primary,
            borderColor: isDark ? 'rgba(255,255,255,0.35)' : alpha(primary, 0.35),
            '&:hover': {
              borderColor: isDark ? '#fff' : primary,
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : alpha(primary, 0.06),
            },
          }}
        >
          Volver al media kit
        </Button>
      </Box>

      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: heroGradient,
          color: '#fff',
          px: { xs: 2.5, sm: 4, md: 8, lg: 12 },
          pt: { xs: 6, md: 10 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 320,
            height: 320,
            borderRadius: '50%',
            top: -120,
            right: -80,
            bgcolor: 'rgba(255,255,255,0.1)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 200,
            height: 200,
            borderRadius: '50%',
            bottom: -60,
            left: '8%',
            bgcolor: 'rgba(255,255,255,0.06)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: { xs: 24, md: 40 },
            right: { xs: 8, md: 48 },
            opacity: 0.35,
            animation: 'aboutFloatA 5s ease-in-out infinite',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <BroadcastScreenVector width={160} />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 16, md: 32 },
            left: { xs: 8, md: 40 },
            opacity: 0.3,
            animation: 'aboutFloatB 6s ease-in-out infinite',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <SignalWavesVector width={100} />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: '42%',
            right: { xs: 4, md: '12%' },
            opacity: 0.28,
            animation: 'aboutFloatB 7s ease-in-out infinite 0.5s',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <NicheGridVector width={120} />
        </Box>

        <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Box
            component="img"
            src={LOGO_LIGHT}
            alt="TNO Network"
            sx={{
              height: { xs: 56, md: 72 },
              width: 'auto',
              display: 'block',
              mb: { xs: 4, md: 5 },
              filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))',
            }}
          />

          <Typography component="p" sx={{ textTransform: 'uppercase', letterSpacing: 3, fontWeight: 700, opacity: 0.9, mb: 2, fontSize: '0.8rem' }}>
            Acerca de TNO Network
          </Typography>

          <Typography component="h1" sx={{ ...heroLineSx, color: '#fff', maxWidth: 900 }}>
            {aboutTnoNetworkContent.heroLines.map((line) => (
              <Box component="span" key={line} display="block">
                {line}
              </Box>
            ))}
          </Typography>

          <Grid container spacing={1.5} sx={{ mt: 4 }}>
            {aboutTnoNetworkContent.stats.map((stat) => (
              <Grid item key={stat.label}>
                <Chip
                  label={
                    <Box component="span">
                      <Box component="span" sx={{ fontWeight: 900, mr: 0.75 }}>
                        {stat.value}
                      </Box>
                      {stat.label}
                    </Box>
                  }
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 600,
                    height: 'auto',
                    py: 0.75,
                    backdropFilter: 'blur(8px)',
                    '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          px: { xs: 2.5, sm: 4, md: 8, lg: 12 },
          py: { xs: 6, md: 10 },
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            background: isDark
              ? `linear-gradient(145deg, ${alpha(primary, 0.2)} 0%, ${alpha(secondary, 0.12)} 100%)`
              : `linear-gradient(145deg, ${alpha(primary, 0.06)} 0%, ${alpha(secondary, 0.1)} 100%)`,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : alpha(primary, 0.15)}`,
            boxShadow: isDark ? 'none' : '0 20px 50px rgba(15,23,42,0.06)',
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.45rem', md: '1.85rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: fg,
              mb: 2,
            }}
          >
            {aboutTnoNetworkContent.subheadline}
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.12rem' }, lineHeight: 1.75, color: muted }}>
            {aboutTnoNetworkContent.intro}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 2.5, sm: 4, md: 8, lg: 12 },
          pb: { xs: 6, md: 10 },
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1rem', md: '1.15rem' },
            mb: { xs: 4, md: 6 },
            color: muted,
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          {aboutTnoNetworkContent.throughLabel}
        </Typography>

        <Grid container spacing={3}>
          {aboutTnoNetworkContent.pillars.map((pillar) => (
            <Grid item xs={12} key={pillar.title}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' },
                  gap: { xs: 2, md: 3 },
                  borderRadius: 4,
                  overflow: 'hidden',
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : alpha(pillar.accent, 0.2)}`,
                  boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.25)' : `0 16px 40px ${alpha(pillar.accent, 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(160deg, ${pillar.accent} 0%, ${alpha(pillar.accent, 0.75)} 100%)`,
                    px: { xs: 3, md: 3.5 },
                    py: { xs: 3, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'center',
                    minWidth: { md: 200 },
                  }}
                >
                  <AboutPillarIllustration type={pillar.illustration} width={100} color="#ffffff" />
                  <Typography
                    component="h2"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.35rem', md: '1.5rem' },
                      color: '#fff',
                      mt: 2,
                      lineHeight: 1.15,
                    }}
                  >
                    {pillar.title}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 3, md: 4 },
                    bgcolor: isDark ? 'transparent' : pillar.accentLight,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.1rem' }, lineHeight: 1.8, color: muted }}>
                    {pillar.body}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box
        sx={{
          px: { xs: 2.5, md: 8 },
          pb: { xs: 8, md: 12 },
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          justifyContent: 'center',
          maxWidth: 1100,
          mx: 'auto',
        }}
      >
        <Button
          component={RouterLink}
          to="/programacion"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            borderRadius: 999,
            px: 3,
            fontWeight: 800,
            background: heroGradient,
            color: '#fff',
            boxShadow: `0 8px 28px ${alpha(primary, 0.35)}`,
            '&:hover': { background: heroGradient, filter: 'brightness(1.08)' },
          }}
        >
          {aboutTnoNetworkContent.ctaGrilla}
        </Button>
        <Button
          component={RouterLink}
          to="/"
          variant="outlined"
          color="primary"
          size="large"
          sx={{ borderRadius: 999, px: 3, fontWeight: 700, borderWidth: 2 }}
        >
          {aboutTnoNetworkContent.ctaMediaKit}
        </Button>
      </Box>

      <Box
        sx={{
          py: 4,
          textAlign: 'center',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
          bgcolor: isDark ? '#020617' : alpha(primary, 0.03),
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="TNO Network"
          sx={{ height: 40, width: 'auto', opacity: isDark ? 0.9 : 0.85, mb: 1 }}
        />
        <Typography variant="caption" sx={{ color: muted, display: 'block' }}>
          Radio visual · Streaming · TNO Digest · Media Lab
        </Typography>
      </Box>
    </Box>
  );
};

LandingAboutTNO.propTypes = {
  themeMode: PropTypes.string,
};

LandingAboutTNO.defaultProps = {
  themeMode: 'light',
};

export default LandingAboutTNO;
